import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.js";
import { useAuth } from "../context/AuthContext.js";

interface PortfolioSummary {
  total_cap_microeur: string | null;
  reserved_microeur: string | null;
  committed_microeur: string | null;
  influencers?: { id: string; name: string; status: string }[];
}

interface Influencer { id: string; name: string; status: string; slug: string; }

function Card({ title, value, sub, color }: { title: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: "#fff", borderRadius: 10, padding: 20, flex: "1 1 200px", minWidth: 180, borderLeft: `4px solid ${color || "#2d6a4f"}` }}>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: "#1a1a1a" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function microeurToStr(v: string | null | undefined) {
  if (!v) return "—";
  return `€${(Number(v) / 1_000_000).toFixed(2)}`;
}

export function DashboardPage() {
  const { organizationId } = useAuth();
  const [summary, setSummary] = useState<PortfolioSummary | null>(null);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<PortfolioSummary>("/portfolio/summary").catch(() => null),
      api.get<{ influencers: Influencer[] }>("/influencers?limit=6").catch(() => ({ influencers: [] })),
    ]).then(([sum, inf]) => {
      setSummary(sum);
      setInfluencers(inf?.influencers ?? []);
    }).finally(() => setLoading(false));
  }, [organizationId]);

  if (loading) return <div style={{ color: "#888", padding: 40 }}>Cargando…</div>;

  const statusColor = (s: string) => s === "active" ? "#2d6a4f" : s === "paused" ? "#d97706" : "#9ca3af";

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Resumen</h1>
      <p style={{ color: "#666", marginBottom: 24, fontSize: 14 }}>Estado global de la organización</p>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
        <Card title="Cap. total" value={microeurToStr(summary?.total_cap_microeur)} sub="límite presupuestario" />
        <Card title="Reservado" value={microeurToStr(summary?.reserved_microeur)} sub="en generación" color="#d97706" />
        <Card title="Comprometido" value={microeurToStr(summary?.committed_microeur)} sub="gastado confirmado" color="#dc2626" />
        <Card title="Disponible" value={
          summary ? microeurToStr(String(
            Math.max(0, Number(summary.total_cap_microeur ?? 0) - Number(summary.reserved_microeur ?? 0) - Number(summary.committed_microeur ?? 0))
          )) : "—"
        } sub="libre para nuevas sesiones" color="#1d4ed8" />
      </div>

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 300px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h2 style={{ fontSize: 16, fontWeight: 600 }}>Influencers</h2>
            <Link to="/influencers" style={{ fontSize: 13, color: "#2d6a4f" }}>Ver todas →</Link>
          </div>
          {influencers.length === 0 ? (
            <div style={{ background: "#fff", borderRadius: 10, padding: 24, color: "#888", textAlign: "center" }}>
              Sin influencers aún. <Link to="/influencers" style={{ color: "#2d6a4f" }}>Crear la primera</Link>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {influencers.map(inf => (
                <Link key={inf.id} to={`/influencers/${inf.id}`} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "#fff", borderRadius: 8, padding: "12px 16px", textDecoration: "none", color: "#1a1a1a"
                }}>
                  <span style={{ fontWeight: 500 }}>{inf.name}</span>
                  <span style={{ fontSize: 11, background: statusColor(inf.status) + "22", color: statusColor(inf.status), padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>
                    {inf.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div style={{ flex: "1 1 280px" }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Accesos rápidos</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              { to: "/sessions", icon: "🎬", label: "Nueva sesión" },
              { to: "/ideas", icon: "💡", label: "Generar ideas" },
              { to: "/catalog", icon: "🛍", label: "Añadir prenda" },
              { to: "/settings/providers", icon: "⚙", label: "Configurar proveedores" },
            ].map(item => (
              <Link key={item.to} to={item.to} style={{
                display: "flex", alignItems: "center", gap: 10, background: "#fff",
                borderRadius: 8, padding: "12px 16px", textDecoration: "none", color: "#1a1a1a"
              }}>
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <span style={{ fontWeight: 500, fontSize: 14 }}>{item.label}</span>
                <span style={{ marginLeft: "auto", color: "#ccc" }}>→</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
